
import type { LiveOpsCurrency, LiveOpsEventTemplate, LiveOpsUserEvent } from '@/types/live-ops'

export enum ParentToIframeMessageType {
  INIT_DATA = 'INIT_DATA',
  PROGRESS_UPDATE = 'PROGRESS_UPDATE',
  PAYMENT_RESULT = 'PAYMENT_RESULT',
  PAYMENT_CANCELLED = 'PAYMENT_CANCELLED',
}

export enum IframeToParentMessageType {
  READY = 'READY',
  ACTION = 'ACTION',
  CLOSE = 'CLOSE',
  DO_HAPTIC = 'DO_HAPTIC',
}

// ============ Data Structures ============

export type LiveOpsStepReward = {
  currency: string
  amount: number
}

export type LiveOpsInitData = {
  config: LiveOpsEventTemplate
  currencies: LiveOpsCurrency[]
  userEvent: LiveOpsUserEvent
  balances: Record<string, number>
  goldenCoinValue: number
}

// ============ Parent -> Iframe Messages ============

export type InitDataMessage = {
  type: ParentToIframeMessageType.INIT_DATA
  payload: LiveOpsInitData
}

export type ProgressUpdateMessage = {
  type: ParentToIframeMessageType.PROGRESS_UPDATE
  payload: {
    userEvent: LiveOpsUserEvent
    balances: Record<string, number>
    goldenCoinValue: number
  }
}

export type PaymentResultMessage = {
  type: ParentToIframeMessageType.PAYMENT_RESULT
  payload: {
    success: boolean
    stepNumber: number
    reward?: LiveOpsStepReward[]
    error?: string
  }
}

export type PaymentCancelledMessage = {
  type: ParentToIframeMessageType.PAYMENT_CANCELLED
  payload: {
    action: Record<string, unknown>
  }
}

export type ParentToIframeMessage = InitDataMessage | ProgressUpdateMessage | PaymentResultMessage | PaymentCancelledMessage

// ============ Iframe -> Parent Messages ============

export type ReadyMessage = {
  type: IframeToParentMessageType.READY
}

export type ActionMessage = {
  type: IframeToParentMessageType.ACTION
  payload: Record<string, unknown>
}

export type CloseMessage = {
  type: IframeToParentMessageType.CLOSE
}

export type DoHapticMessage = {
  type: IframeToParentMessageType.DO_HAPTIC
  payload?: {
    style?: 'light' | 'medium' | 'heavy'
  }
}

export type IframeToParentMessage = ReadyMessage | ActionMessage | CloseMessage | DoHapticMessage

// ============ Type Guards ============

export function isIframeToParentMessage(data: unknown): data is IframeToParentMessage {
  if (typeof data !== 'object' || data === null) return false
  const message = data as { type?: unknown }
  return typeof message.type === 'string' && Object.values(IframeToParentMessageType).includes(message.type as IframeToParentMessageType)
}

export function isReadyMessage(message: IframeToParentMessage): message is ReadyMessage {
  return message.type === IframeToParentMessageType.READY
}

export function isActionMessage(message: IframeToParentMessage): message is ActionMessage {
  return message.type === IframeToParentMessageType.ACTION
}

export function isCloseMessage(message: IframeToParentMessage): message is CloseMessage {
  return message.type === IframeToParentMessageType.CLOSE
}

export function isDoHapticMessage(message: IframeToParentMessage): message is DoHapticMessage {
  return message.type === IframeToParentMessageType.DO_HAPTIC
}
